import * as ast from '../generated/ast.js';
import { RichAssetTypesDisplayNames } from './mappings.js';
import { isValidGlobalVariableName, getIntRef } from './utilities.js';
import { ANY_NODE_STRING } from './constants.js';
export class NodeFields {
    constructor(node) {
        // Extracted/Calculated values for the node
        this.globalVariables = [];
        this.childEdgeNodeNumbers = [];
        // Tags generated for all child edges. The key would be the
        // node number of the outgoing tag, and the value for it would be
        // in the format {int-ref}*{uuid}
        this.outgoingTags = {};
        this.incomingTags = {};
        this.getOutgoingTagForNode = (nodeNumber) => {
            if (nodeNumber) {
                return this.outgoingTags[nodeNumber];
            }
            return '';
        };
        this.getOutgoingCodeForNode = (nodeNumber) => {
            if (nodeNumber) {
                const tag = this.outgoingTags[nodeNumber];
                // remove the int_ref (32 char guid) and * from the start of the string and
                // only return the last code portion of the tag
                return tag.substring(33);
            }
            return '';
        };
        this.addIncomingTag = (nodeNumber, incomingTag) => {
            this.incomingTags[nodeNumber] = incomingTag;
        };
        this.populateFields = () => {
            this.refNode?.fields?.forEach((field) => {
                switch (true) {
                    case ast.isAnswerRequired(field):
                        this.answerRequiredField = field;
                        break;
                    case ast.isBehaviors(field):
                        this.behaviorsField = field;
                        break;
                    case ast.isCommand(field):
                        this.commandField = field;
                        break;
                    case ast.isCssClassName(field):
                        this.cssClassNameField = field;
                        break;
                    case ast.isDecisionVariable(field):
                        this.decisionVariableField = field;
                        break;
                    case ast.isDescription(field):
                        this.descriptionField = field;
                        break;
                    case ast.isEvent(field):
                        this.eventField = field;
                        break;
                    case ast.isFlows(field):
                        this.flowsField = field;
                        break;
                    case ast.isGroupName(field):
                        this.groupNameField = field;
                        break;
                    case ast.isGroupOrder(field):
                        this.groupOrderField = field;
                        break;
                    case ast.isId(field):
                        this.idField = field;
                        break;
                    case ast.isIntent(field):
                        this.intentField = field;
                        break;
                    case ast.isMessage(field):
                        this.messageField = field;
                        break;
                    case ast.isNextNodes(field):
                        this.nextNodesField = field;
                        break;
                    case ast.isNluDisabled(field):
                        this.nluDisabledField = field;
                        break;
                    case ast.isNodeInputs(field):
                        this.nodeInputsField = field;
                        break;
                    case ast.isNodeName(field):
                        this.nodeNameField = field;
                        break;
                    case ast.isNodeTags(field):
                        this.nodeTagsField = field;
                        break;
                    case ast.isNodeType(field):
                        this.nodeTypeField = field;
                        break;
                    case ast.isOutput(field):
                        this.outputField = field;
                        break;
                    case ast.isVariables(field):
                        this.variablesField = field;
                        break;
                    case ast.isParameterInput(field):
                        this.parameterInputField = field;
                        break;
                    case ast.isPlatformFlag(field):
                        this.platformFlagField = field;
                        break;
                    case ast.isRichAsset(field):
                        this.richAssetField = field;
                        break;
                    case ast.isSkillTag(field):
                        this.skillTagField = field;
                        break;
                    case ast.isUtterances(field):
                        this.utterancesField = field;
                        break;
                    case ast.isWhatsNext(field):
                        this.whatsNextField = field;
                        break;
                    default:
                        console.log('Unknow field encountered.');
                        console.log(field);
                        break;
                }
            });
        };
        this.populateGlobalVariables = () => {
            // If we have variables defined, determine if they are global variables
            // or utterance variables, and put them in the appropriate array for reference
            if (this.variablesField?.variables &&
                this.variablesField.variables.length > 0) {
                const { variables } = this.variablesField;
                // Check if these are utterance variable or regular global variables
                // The condition for utterance variables are the following.
                // - nodeType is D
                // - answerRequired is true
                // - richAssetType is none
                let isUtteranceVariable = false;
                if (this.nodeTypeField?.nodeType?.value === 'D' &&
                    this.answerRequiredField?.answerRequired === 'true' &&
                    (!this.richAssetField ||
                        ast.isRichAssetNone(this.richAssetField?.content))) {
                    isUtteranceVariable = true;
                }
                variables.forEach((variable) => {
                    if (isValidGlobalVariableName(variable.value)) {
                        if (isUtteranceVariable) {
                            // Decision nodes are only allowed one global variable so we
                            // won't ever have more than one utterance variable in the node
                            // In case more are specified, that would fail the global variables
                            // validation check for the node.
                            this.utteranceVariable = variable.value;
                        }
                        else {
                            this.globalVariables.push(variable.value);
                        }
                    }
                });
            }
            // If this is a decision node with buttons rich asset type, then
            // global variables can also be defined inside the buttons json
            if (this.nodeTypeField &&
                this.nodeTypeField.nodeType.value === 'D' &&
                this.richAssetField &&
                ast.isRichAssetButtons(this.richAssetField.content) &&
                ast.isButtonsJson(this.richAssetField.content.value)) {
                const buttonsJson = this.richAssetField.content.value;
                const buttonOptionsArray = buttonsJson.properties.find((prop) => ast.isButtonOptionsArrayProperty(prop));
                buttonOptionsArray?.options?.forEach((option) => {
                    const variablesArray = option.properties.find((optionProp) => ast.isVariablesArrayProperty(optionProp));
                    variablesArray?.variables?.forEach((variableObject) => {
                        const variableNameProperty = variableObject.properties.find((p) => ast.isVariableProperty(p));
                        const variableName = variableNameProperty?.variable?.value;
                        if (variableName && isValidGlobalVariableName(variableName)) {
                            this.globalVariables.push(variableName);
                        }
                    });
                });
            }
        };
        this.serialize = () => {
            const serializableNodeFields = {
                id: this.idField?.id?.value || `node-${this.refNode.name}`,
                nodeNumber: this.refNode.name,
                nodeType: this.nodeTypeField?.nodeType?.value || '',
                nodeName: this.nodeNameField?.nodeName &&
                    ast.isStringField(this.nodeNameField.nodeName)
                    ? this.nodeNameField.nodeName.value
                    : undefined,
                answerRequired: this.answerRequiredField?.answerRequired === 'true' ? true : false,
                behaviors: this.behaviorsField?.behaviors?.map((b) => b.value),
                command: this.commandField?.command &&
                    ast.isStringField(this.commandField.command)
                    ? this.commandField.command.value
                    : undefined,
                cssClassName: this.cssClassNameField?.cssClassName &&
                    ast.isStringField(this.cssClassNameField.cssClassName)
                    ? this.cssClassNameField.cssClassName.value
                    : undefined,
                decisionVariable: this.decisionVariableField?.decisionVariable &&
                    ast.isStringField(this.decisionVariableField.decisionVariable)
                    ? this.decisionVariableField.decisionVariable.value
                    : undefined,
                description: this.descriptionField?.description &&
                    ast.isStringField(this.descriptionField.description)
                    ? this.descriptionField.description.value
                    : undefined,
                flows: this.flowsField?.flows?.map((f) => f.value),
                groupName: this.groupNameField?.groupName &&
                    ast.isStringField(this.groupNameField.groupName)
                    ? this.groupNameField.groupName.value
                    : undefined,
                groupOrder: this.groupOrderField?.groupOrder &&
                    ast.isNumbersField(this.groupOrderField.groupOrder)
                    ? Number(this.groupOrderField.groupOrder.value)
                    : undefined,
                intent: this.intentField?.intent && ast.isStringField(this.intentField.intent)
                    ? this.intentField.intent.value
                    : undefined,
                message: this.messageField?.message &&
                    ast.isStringField(this.messageField.message)
                    ? this.messageField.message.value
                    : undefined,
                nextNodes: this.nextNodesField?.nextNodes
                    ?.map((nn) => nn.ref?.name)
                    .filter((name) => name !== undefined),
                nluDisabled: this.nluDisabledField?.nluDisabled === 'true' ? true : false,
                nodeInput: this.nodeInputsField?.nodeInput?.value,
                nodeTags: this.nodeTagsField?.nodeTags &&
                    ast.isStringField(this.nodeTagsField.nodeTags)
                    ? this.nodeTagsField.nodeTags.value
                    : undefined,
                output: this.outputField?.output?.map((o) => o.value),
                variables: this.variablesField?.variables?.map((v) => v.value),
                /* parameterInput: this.parameterInputField?.parameterInput
                  ? JSON.stringify(this.parameterInputField.parameterInput)
                  : undefined,*/
                platformFlag: this.platformFlagField?.platformFlag &&
                    ast.isStringField(this.platformFlagField.platformFlag)
                    ? this.platformFlagField.platformFlag.value
                    : undefined,
                richAsset: this.richAssetField
                    ? {
                        type: RichAssetTypesDisplayNames[this.richAssetField.content.$type],
                        content: '',
                    }
                    : undefined,
                skillTag: this.skillTagField?.skillTag &&
                    ast.isStringField(this.skillTagField.skillTag)
                    ? this.skillTagField.skillTag.value
                    : undefined,
                utterances: this.utterancesField?.utterances?.map((u) => u.value),
                whatsNext: this.whatsNextField?.whatsNext &&
                    ast.isStringField(this.whatsNextField.whatsNext)
                    ? this.whatsNextField.whatsNext.value
                    : undefined,
            };
            return serializableNodeFields;
        };
        this.populateChildEdgeNodes = () => {
            const childEdgeNodeNumbers = [];
            // If this is an Action node then the whatsNext
            // field gives us the child edges from this node
            if (this.nodeTypeField?.nodeType?.value === 'A' &&
                this.whatsNextField?.whatsNext &&
                ast.isStringField(this.whatsNextField?.whatsNext)) {
                const whatsNextString = this.whatsNextField.whatsNext.value.trim();
                if (whatsNextString !== ANY_NODE_STRING) {
                    const whatsNextItems = whatsNextString.split('|');
                    whatsNextItems.forEach((whatsNextItem) => {
                        const indexOfTilde = whatsNextItem.indexOf('~');
                        if (indexOfTilde !== -1) {
                            const nodeNumber = whatsNextItem.slice(indexOfTilde + 1).trim();
                            childEdgeNodeNumbers.push(nodeNumber);
                        }
                    });
                }
            }
            // If this is a decision node then there are multiple options
            // for navigation from this node that we need to check.
            // 1. the nextNodes field
            // 2. any rich asset content with dest properties
            // For rich assets, do not consider persisten_menu and dynamic embeds
            if (this.nodeTypeField?.nodeType?.value === 'D') {
                // If it has nodes defined in the nextNodes field
                if (this.nextNodesField?.nextNodes &&
                    this.nextNodesField?.nextNodes?.length > 0) {
                    this.nextNodesField.nextNodes.forEach((nextNode) => {
                        const referenceNode = nextNode.ref;
                        if (referenceNode) {
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has a buttons rich asset that has dest properties in it
                const richAssetContent = this.richAssetField?.content;
                if (ast.isRichAssetButtons(richAssetContent) &&
                    ast.isButtonsJson(richAssetContent.value)) {
                    const buttonsJson = richAssetContent.value;
                    // Get the options array from the properties
                    const optionsArray = buttonsJson.properties.find((p) => ast.isButtonOptionsArrayProperty(p));
                    optionsArray?.options?.forEach((option) => {
                        // Find the dest property in the option
                        const destProperty = option.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has a list picker rich asset that has dest properties in it
                if (ast.isRichAssetListPicker(richAssetContent) &&
                    ast.isListPickerJson(richAssetContent.value)) {
                    const listPickerJson = richAssetContent.value;
                    // Get the options array from the properties
                    const optionsArray = listPickerJson.properties.find((p) => ast.isListPickerOptionsArrayProperty(p));
                    optionsArray?.options?.forEach((option) => {
                        // Find the dest property in the option
                        const destProperty = option.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has an image button rich asset that has dest properties in it
                if (ast.isRichAssetImageButton(richAssetContent) &&
                    ast.isImageButtonJson(richAssetContent.value)) {
                    const imageButtonJson = richAssetContent.value;
                    // Get the options array from the properties
                    const optionsArray = imageButtonJson.properties.find((p) => ast.isImageButtonOptionsArrayProperty(p));
                    optionsArray?.options?.forEach((option) => {
                        // Find the dest property in the option
                        const destProperty = option.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has a carousel rich asset that has dest properties in it
                if (ast.isRichAssetCarousel(richAssetContent) &&
                    ast.isCarouselJson(richAssetContent.value)) {
                    const carouselJson = richAssetContent.value;
                    carouselJson.items.forEach((item) => {
                        // Find the dest property in the item
                        const destProperty = item.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                        // Each item can also have additional cta with dest property
                        const additionalCtaProperty = item.properties.find((p) => ast.isAdditionalCtaArrayProperty(p));
                        if (additionalCtaProperty && additionalCtaProperty.items.length > 0) {
                            additionalCtaProperty.items.forEach((ctaItem) => {
                                // Find the dest property in the item
                                const ctaDestProperty = ctaItem.properties.find((p) => ast.isDestProperty(p));
                                if (ctaDestProperty && ctaDestProperty.dest?.ref) {
                                    const referenceNode = ctaDestProperty.dest.ref;
                                    childEdgeNodeNumbers.push(referenceNode.name);
                                }
                            });
                        }
                    });
                }
                // If it has a star rich asset that has dest properties in it
                if (ast.isRichAssetStar(richAssetContent) &&
                    ast.isStarJson(richAssetContent.value)) {
                    const starJson = richAssetContent.value;
                    // Find the options property in the star json
                    const optionsArray = starJson.properties.find((p) => ast.isStarOptionsArrayProperty(p));
                    if (optionsArray && optionsArray.options.length > 0) {
                        optionsArray.options.forEach((option) => {
                            const { destProperty } = option;
                            if (destProperty && destProperty.dest?.ref) {
                                const referenceNode = destProperty.dest.ref;
                                childEdgeNodeNumbers.push(referenceNode.name);
                            }
                        });
                    }
                }
                // If it has a persistent menu rich asset that has dest properties in it
                if (ast.isRichAssetPersistentMenu(richAssetContent) &&
                    ast.isPersistentMenuJson(richAssetContent.value)) {
                    const persistentMenuJson = richAssetContent.value;
                    // Find the options array from the properties
                    const optionsArray = persistentMenuJson.properties.find((p) => ast.isPersistentMenuOptionsArrayProperty(p));
                    optionsArray?.options?.forEach((option) => {
                        // Find the dest property in the option
                        const destProperty = option.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has a quick reply rich asset that has dest properties in it
                if (ast.isRichAssetQuickReply(richAssetContent) &&
                    ast.isQuickReplyJson(richAssetContent.value)) {
                    const quickReplyJson = richAssetContent.value;
                    // Find the options array from the properties
                    const optionsArray = quickReplyJson.properties.find((p) => ast.isQuickReplyOptionsArrayProperty(p));
                    optionsArray?.options?.forEach((option) => {
                        // Find the dest property in the option
                        const destProperty = option.properties.find((p) => ast.isDestProperty(p));
                        if (destProperty && destProperty.dest?.ref) {
                            const referenceNode = destProperty.dest.ref;
                            childEdgeNodeNumbers.push(referenceNode.name);
                        }
                    });
                }
                // If it has a button rich asset that has node reference in it
                if (ast.isRichAssetButton(richAssetContent) && richAssetContent.value) {
                    // If it is a static button, then get the node number from the button items
                    const buttonString = richAssetContent.value;
                    if (buttonString.startsWith('{') === false) {
                        const buttons = buttonString.split('|');
                        buttons.forEach((button) => {
                            const indexOfTilde = button.indexOf('~');
                            if (indexOfTilde !== -1) {
                                // Get the portion after ~ as that is the destination node number
                                const nodeNumber = button.slice(indexOfTilde + 1).trim();
                                childEdgeNodeNumbers.push(nodeNumber);
                            }
                        });
                    }
                }
                // If it has a button rich asset that has node reference in it
                if (ast.isRichAssetSelection(richAssetContent) &&
                    richAssetContent.value) {
                    // If it is a static button, then get the node number from the button items
                    const selectionString = richAssetContent.value;
                    if (selectionString.startsWith('{') === false) {
                        const selections = selectionString.split('|');
                        selections.forEach((selection) => {
                            const indexOfTilde = selection.indexOf('~');
                            if (indexOfTilde !== -1) {
                                // Get the portion after ~ as that is the destination node number
                                const nodeNumber = selection.slice(indexOfTilde + 1).trim();
                                childEdgeNodeNumbers.push(nodeNumber);
                            }
                        });
                    }
                }
            }
            // Get the unique node numbers and set them in the class variable
            this.childEdgeNodeNumbers = [...new Set(childEdgeNodeNumbers)];
        };
        this.generateOutgoingTags = () => {
            this.childEdgeNodeNumbers.forEach((outgoingNodeNumber) => {
                // Set the outgoing tags on the nodeFields object
                this.outgoingTags[outgoingNodeNumber] = `${this.intRef}*${getIntRef()}`;
            });
        };
        this.refNode = node;
        this.nodeNumber = node.name;
        this.populateFields();
        // Generate the intRef for this node from the idField
        // if it is present. Otherwise we will use a random guid
        this.intRef = getIntRef(this.idField);
        // Populate the list of global/utterance variables
        this.populateGlobalVariables();
        // Populate the list of all the child edge nodes
        this.populateChildEdgeNodes();
        // After generating the child edge nodes, generate
        // outgoing tags based on them
        this.generateOutgoingTags();
    }
}
//# sourceMappingURL=node-fields.js.map